import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/apiService';

const AuthContext = createContext(null);

// Session marker, shared by all tabs: a new value on every change of signed-in account
// (sign-in, « agir en tant que » start and exit, group administration, group switch),
// removed on sign-out. Token refreshes never touch it. The API client reads it to drop
// requests answered after a change (same key in services/apiService.js), and other
// tabs reload when it changes, so no tab keeps showing one account with another's token.
const SESSION_KEY = 'authSession';

function markNewSession() {
  localStorage.setItem(SESSION_KEY, `${Date.now()}-${Math.random().toString(36).slice(2)}`);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      if (data.user?.impersonating) {
        setImpersonating(true);
      }
    } catch {
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('originalToken');
      localStorage.removeItem(SESSION_KEY);
      setImpersonating(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchMe();
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  // Another tab changed the signed-in account: start again from what is stored now
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === SESSION_KEY) window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // One way in for every login-shaped answer: signing in, and starting or leaving
  // « agir en tant que ». The token is stored before the user changes, so the socket
  // (which reconnects on every new user) always dials with the new token.
  const applySession = useCallback((data) => {
    localStorage.setItem('token', data.accessToken);
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
    } else {
      // Never keep the previous account's refresh token behind a new session
      localStorage.removeItem('refreshToken');
    }
    markNewSession();
    // Keep the sign-in flags in the session too, so /login still knows where to send
    // this user until the page reloads (and /auth/me answers instead)
    const signedInUser = {
      ...data.user,
      must_change_password: !!data.must_change_password,
      must_select_group: !!data.must_select_group,
    };
    setUser(signedInUser);
    return signedInUser;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    return applySession(data);
  }, [applySession]);

  const register = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/register', { email, password });
    localStorage.setItem('token', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    markNewSession();
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('originalToken');
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
    setImpersonating(false);
  }, []);

  const updateGroupLogo = useCallback((logoUrl) => {
    setUser((prev) => prev ? { ...prev, group_logo: logoUrl } : prev);
  }, []);

  const impersonateGroup = useCallback(async (groupId) => {
    const currentToken = localStorage.getItem('token');
    localStorage.setItem('originalToken', currentToken);
    const { data } = await api.post(`/admin/groups/${groupId}/impersonate`);
    localStorage.setItem('token', data.token);
    markNewSession();
    setUser(data.user);
    setImpersonating(true);
    return data;
  }, []);

  const exitImpersonation = useCallback(async () => {
    const { data } = await api.post('/admin/exit-impersonate');
    localStorage.setItem('token', data.token);
    localStorage.removeItem('originalToken');
    markNewSession();
    setUser(data.user);
    setImpersonating(false);
    return data;
  }, []);

  // Super admin only: see and use the site as one member. The answer is a fresh
  // member session (user.act_as set); the exit answer is a fresh super admin session.
  const startActAs = useCallback(async (memberId) => {
    const { data } = await api.post(`/admin/members/${memberId}/act-as`);
    localStorage.removeItem('originalToken');
    setImpersonating(false);
    return applySession(data);
  }, [applySession]);

  const exitActAs = useCallback(async () => {
    const { data } = await api.post('/auth/act-as/exit');
    return applySession(data);
  }, [applySession]);

  // « Se déconnecter » chosen by the user. While acting as a member, the mode is left
  // on the server first so it is recorded as ended; the session is cleared either way.
  const signOut = useCallback(async () => {
    if (user?.act_as) {
      try {
        await api.post('/auth/act-as/exit');
      } catch {
        // Best effort: never keep the user signed in because the exit call failed
      }
    }
    logout();
  }, [user, logout]);

  // Switch group for multigroup users (members / group_admin with >1 groups)
  // or for supervisors (endpoint = '/supervisor')
  const switchGroup = useCallback(async (groupId, endpoint = '/auth') => {
    const { data } = await api.post(`${endpoint}/switch-group/${groupId}`);
    if (data.accessToken) localStorage.setItem('token', data.accessToken);
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    markNewSession();
    setUser(data.user);
    return data;
  }, []);

  // canWrite — false for supervisor role (read-only view)
  const canWrite = user?.role !== 'supervisor';

  // Derived from the user (also after a reload, from /auth/me), never from a stored flag
  const actAs = user?.act_as ?? null;

  const value = useMemo(
    () => ({ user, loading, login, register, logout, signOut, fetchMe, updateGroupLogo, impersonating, impersonateGroup, exitImpersonation, switchGroup, canWrite, actAs, startActAs, exitActAs }),
    [user, loading, login, register, logout, signOut, fetchMe, updateGroupLogo, impersonating, impersonateGroup, exitImpersonation, switchGroup, canWrite, actAs, startActAs, exitActAs]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
